import { useMemo } from "react"

import DOMPurify from "dompurify"
import { marked } from "marked"
import { useTranslation } from "react-i18next"

import { Spinner } from "@/components/ui/spinner"
import { parseAnswerToHtml } from "@/features/chat/components/answer-parser"
import { useDocument } from "@/hooks/use-documents"

export function DocPreviewContent({ documentId }: { documentId: string }) {
  const { t } = useTranslation()
  const { data: document, isLoading } = useDocument(documentId)

  const contentHtml = useMemo(() => {
    if (!document?.content) return ""
    const rawMarkdown = parseAnswerToHtml({
      answer: document.content,
      isStreaming: false,
      noCitation: true,
      citationSection: documentId,
    }).answerHtml

    // Documents are shareable across users (artifact sharing), so their
    // content must be treated as untrusted: convert markdown to HTML and
    // sanitize before injecting, mirroring the marked+DOMPurify pattern
    // used elsewhere for untrusted HTML (e.g. EmailDraftRenderer).
    marked.setOptions({ gfm: true, breaks: false })
    const parsed = marked.parse(rawMarkdown)
    const html = typeof parsed === "string" ? parsed : ""
    return DOMPurify.sanitize(html)
  }, [document?.content, documentId])

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="size-6" />
      </div>
    )
  }

  if (!document) {
    return (
      <div className="text-muted-foreground p-6 text-sm">
        {t("documents.not_found_description", {
          defaultValue: "This document doesn't exist or you don't have access to it.",
        })}
      </div>
    )
  }

  return (
    <div className="overflow-y-auto px-6 py-4">
      <div
        className="prose dark:prose-invert max-w-none text-sm"
        dangerouslySetInnerHTML={{ __html: contentHtml }}
      />
    </div>
  )
}
